import { getAuthToken, getStoredUser, getAuthHeaders } from '@/utils/auth';
import { clearAuth } from '@/utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StyledSelect } from '@/components/ui/styled-select';
import { 
  Building2, TrendingUp, Users, DollarSign, Plus, X, 
  LogOut, MapPin, Calendar, Home, BarChart3, ArrowLeft, User, Upload, Pencil, Trash2, CheckSquare, Square
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '@/context/CurrencyContext';
import PartnerCRM from '@/components/PartnerCRM';
import PartnerCRMDashboardV2 from '@/components/PartnerCRMDashboardV2';
import Nav from '@/components/Nav';
import BulkUpload from '@/components/BulkUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Luxury Real Estate Background Images
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1768069794857-9306ac167c6e?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1771335107479-429c5e06420f?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1768069803726-be26937ec6d8?auto=format&fit=crop&w=1920&q=80'
];

const DeveloperDashboard = () => {
  const navigate = useNavigate();
  const { formatPrice: formatCurrency } = useCurrency();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [properties, setProperties] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [editingProperty, setEditingProperty] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  // Per-listing completion-% for v2 wizard items (keyed by property id).
  const [completion, setCompletion] = useState({});

  const [newProperty, setNewProperty] = useState({
    project_name: '', location: '', property_type: 'apartment', total_units: 100,
    available_units: 100, price_from: 1000000, price_to: 5000000, completion_date: '',
    amenities: [], description: ''
  });

  const handleLogout = useCallback(() => {
    clearAuth();
    navigate('/partner/login');
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [propsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/partner/developer/properties`, { headers }),
        axios.get(`${API}/partner/developer/analytics`, { headers })
      ]);

      setProperties(propsRes.data);
      setAnalytics(analyticsRes.data);

      // Fetch completion-% for every property (legacy + v2 wizard).
      const all = propsRes.data || [];
      if (all.length === 0) {
        setCompletion({});
      } else {
        const entries = await Promise.all(all.map(async (p) => {
          try {
            const { data } = await axios.post(
              `${API}/partner-listings/property/${p.id}/validate`,
              null,
              { headers },
            );
            return [p.id, data];
          } catch { return [p.id, null]; }
        }));
        const map = {};
        entries.forEach(([id, rep]) => { if (rep) map[id] = rep; });
        setCompletion(map);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired');
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    const userData = JSON.stringify(getStoredUser());
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchData();
    
    // Rotate background images
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleCreateProperty = async () => {
    try {
      const token = getAuthToken();
      if (editingProperty) {
        await axios.put(`${API}/partner/developer/properties/${editingProperty.id}`, newProperty, {
          headers: getAuthHeaders()
        });
        toast.success('Property updated!');
      } else {
        await axios.post(`${API}/partner/developer/properties`, newProperty, {
          headers: getAuthHeaders()
        });
        toast.success('Property created!');
      }
      setShowAddModal(false);
      setEditingProperty(null);
      setNewProperty({
        project_name: '', location: '', property_type: 'apartment', total_units: 100,
        available_units: 100, price_from: 1000000, price_to: 5000000, completion_date: '',
        amenities: [], description: ''
      });
      fetchData();
    } catch (error) {
      toast.error(editingProperty ? 'Failed to update property' : 'Failed to create property');
    }
  };

  // Unified adapter — reads both v2 wizard schema (title, cover_image, price,
  // bedrooms, etc.) AND legacy schema (project_name, image_url, price_from,
  // bedrooms_min). New wizard always sets asset_type='property'; absence
  // means it's a legacy doc.
  const adapt = (p) => {
    const isV2 = !!p.asset_type;
    const cover = p.cover_image
      || (Array.isArray(p.gallery_images) && p.gallery_images[0])
      || p.image_url
      || p.image
      || null;
    const locationStr = isV2
      ? [p.area, p.locality, p.city, p.state, p.country].filter(Boolean).join(', ')
      : (p.location || '');
    const priceLabel = (() => {
      if (p.price) return formatCurrency(p.price);
      if (p.price_from || p.price_to) {
        return `${formatCurrency(p.price_from || 0)} – ${formatCurrency(p.price_to || 0)}`;
      }
      return '—';
    })();
    return {
      isV2,
      cover,
      title: p.title || p.project_name || p.name || 'Untitled Property',
      location: locationStr || '—',
      type: p.category || p.property_type || (isV2 ? p.listing_type : '') || '—',
      status: p.status || (p.published ? 'published' : 'draft'),
      priceLabel,
      pricePerSqft: p.price_per_sqft,
      bedrooms: p.bedrooms ?? p.bedrooms_min,
      bathrooms: p.bathrooms,
      parking: p.parking_spaces,
      builtUp: p.builtup_area,
      areaUnit: p.area_unit || 'Sq. Ft.',
      amenities: Array.isArray(p.amenities) ? p.amenities.length : 0,
      smartFeatures: Array.isArray(p.smart_features) ? p.smart_features.length : 0,
      views: Array.isArray(p.views) ? p.views.length : 0,
      videos: Array.isArray(p.videos) ? p.videos.length : 0,
      gallery: Array.isArray(p.gallery_images) ? p.gallery_images.length : 0,
      rera: p.rera_number,
      shortDesc: p.short_description,
      // Legacy fields kept for backward compatibility
      availableUnits: p.available_units,
      totalUnits: p.total_units,
      completionDate: p.completion_date,
    };
  };

  const handleEditProperty = (prop) => {
    // v2 listing → open in the new wizard; legacy → legacy inline modal
    if (prop.asset_type) {
      navigate(`/partner/listings/property/${prop.id}`);
      return;
    }
    setEditingProperty(prop);
    setNewProperty({
      project_name: prop.project_name || '', location: prop.location || '',
      property_type: prop.property_type || 'apartment', total_units: prop.total_units || 0,
      available_units: prop.available_units || 0, price_from: prop.price_from || 0,
      price_to: prop.price_to || 0, completion_date: prop.completion_date || '',
      amenities: prop.amenities || [], description: prop.description || ''
    });
    setShowAddModal(true);
  };

  const handleDeleteProperty = async (propertyId) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/partner/developer/properties/${propertyId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Property deleted');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === properties.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(properties.map(p => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    try {
      const token = getAuthToken();
      const ids = Array.from(selectedIds);
      await axios.post(`${API}/partner/developer/properties/bulk-delete`, { ids }, {
        headers: getAuthHeaders()
      });
      toast.success(`${ids.length} properties deleted`);
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete properties');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Navigation */}
      <Nav variant="partner" showBack partnerLabel="Developer Portal" companyName={user?.company_name} />

      {/* Hero Section with Luxury Background */}
      <div className="relative h-[320px] overflow-hidden" data-testid="developer-hero">
        {HERO_IMAGES.map((img, index) => (
          <motion.div
            key={img}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${img})` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentBgIndex ? 1 : 0 }}
            transition={{ duration: 1.5 }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-obsidian" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 md:px-8 h-full flex items-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gold/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-gold" />
              </div>
              <span className="text-gold/80 text-xs sm:text-sm uppercase tracking-widest">Real Estate Developer</span>
            </div>
            <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold mb-3 text-white">Developer Dashboard</h1>
            <p className="text-white/70 text-base sm:text-lg truncate">Welcome back, <span className="text-gold">{user?.name}</span></p>
          </motion.div>
          
          {activeTab === 'properties' && (
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="ml-auto hidden md:flex gap-3"
            >
              <Button onClick={() => setShowBulkUpload(true)} variant="outline" className="border-gold/50 text-gold hover:bg-gold/10" data-testid="bulk-upload-btn">
                <Upload className="w-4 h-4 mr-2" /> Bulk Upload
              </Button>
              <Button onClick={() => navigate('/partner/listings/new/property')} className="bg-gold text-black hover:bg-gold-light" data-testid="add-property-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Property
              </Button>
            </motion.div>
          )}
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8 -mt-16 relative z-20">

        {/* Tabs — horizontal scroll on small screens so they don't push the page off-canvas */}
        <div className="flex gap-2 mb-6 sm:mb-8 bg-white/5 backdrop-blur-sm rounded-xl p-2 overflow-x-auto" data-testid="developer-tabs">
          {['overview', 'properties', 'crm', 'reports'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`tab-${tab}`}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-all duration-300 capitalize font-medium text-sm flex-shrink-0 whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-gradient-to-r from-gold to-gold-light text-black shadow-lg shadow-gold/25'
                  : 'text-text-secondary hover:bg-white/10 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="developer-stats">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} 
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-gold/30 transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-3xl group-hover:bg-gold/20 transition-all" />
                <Building2 className="w-10 h-10 text-gold mb-4 relative z-10" />
                <div className="text-4xl font-bold mb-1 relative z-10">{analytics?.total_projects || 0}</div>
                <div className="text-text-secondary">Active Projects</div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} 
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-gold/30 transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-all" />
                <Home className="w-10 h-10 text-blue-400 mb-4 relative z-10" />
                <div className="text-4xl font-bold mb-1 relative z-10">{analytics?.total_units || 0}</div>
                <div className="text-text-secondary">Total Units</div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} 
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-emerald-500/30 transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl group-hover:bg-emerald-500/20 transition-all" />
                <TrendingUp className="w-10 h-10 text-emerald-400 mb-4 relative z-10" />
                <div className="text-4xl font-bold mb-1 relative z-10">{analytics?.sold_units || 0}</div>
                <div className="text-text-secondary">Units Sold</div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} 
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-gold/30 transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-3xl group-hover:bg-gold/20 transition-all" />
                <BarChart3 className="w-10 h-10 text-gold mb-4 relative z-10" />
                <div className="text-4xl font-bold mb-1 relative z-10">{analytics?.occupancy_rate || 0}%</div>
                <div className="text-text-secondary">Occupancy Rate</div>
              </motion.div>
            </div>

            {/* Recent Properties */}
            <div>
              <h3 className="font-playfair text-2xl font-semibold mb-4">Recent Projects</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.slice(0, 3).map((rawProp, index) => {
                  const v = adapt(rawProp);
                  const pct = completion[rawProp.id]?.total_pct;
                  return (
                  <motion.div key={rawProp.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + index * 0.1 }} className="glass-card overflow-hidden">
                    <div className="aspect-[16/10] bg-white/5 overflow-hidden relative">
                      {v.cover ? (
                        <img src={v.cover} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-text-secondary text-xs uppercase tracking-[0.18em]">No cover</div>
                      )}
                      {typeof pct === 'number' && (
                        <span
                          className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.15em] font-semibold ${
                            pct >= 100 ? 'bg-gold text-black' : 'bg-black/70 backdrop-blur text-white/90 ring-1 ring-white/15'
                          }`}
                          data-testid={`completion-pill-${rawProp.id}`}
                        >
                          {pct}% complete
                        </span>
                      )}
                    </div>
                    <div className="p-6">
                      <h4 className="font-semibold text-lg mb-2 line-clamp-1">{v.title}</h4>
                      <div className="flex items-center gap-2 text-text-secondary text-sm mb-3">
                        <MapPin className="w-4 h-4" />
                        <span className="line-clamp-1">{v.location || v.type}</span>
                      </div>
                      {v.priceLabel && v.priceLabel !== '—' && (
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary text-sm">Price</span>
                          <span className="text-gold font-bold text-sm">{v.priceLabel}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                  );
                })}
                {properties.length === 0 && (
                  <div className="col-span-3 glass-card p-8 text-center text-text-secondary">
                    No projects yet. Add your first property project.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <div>
            {/* Mobile-only Add / Bulk buttons (desktop versions live in the hero strip) */}
            <div className="flex md:hidden gap-2 mb-4">
              <Button onClick={() => setShowBulkUpload(true)} variant="outline" size="sm" className="flex-1 border-gold/50 text-gold hover:bg-gold/10" data-testid="bulk-upload-btn-mobile">
                <Upload className="w-4 h-4 mr-2" /> Bulk Upload
              </Button>
              <Button onClick={() => navigate('/partner/listings/new/property')} size="sm" className="flex-1 bg-gold text-black hover:bg-gold-light" data-testid="add-property-btn-mobile">
                <Plus className="w-4 h-4 mr-2" /> Add Property
              </Button>
            </div>

            {/* Bulk Actions Toolbar */}
            {properties.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6 glass-card p-3 sm:p-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-text-secondary hover:text-white transition-colors" data-testid="select-all-properties">
                    {selectedIds.size === properties.length && properties.length > 0 ? <CheckSquare className="w-5 h-5 text-gold" /> : <Square className="w-5 h-5" />}
                    <span className="hidden sm:inline">{selectedIds.size === properties.length && properties.length > 0 ? 'Deselect All' : 'Select All'}</span>
                  </button>
                  {selectedIds.size > 0 && (
                    <span className="text-sm text-gold font-medium">{selectedIds.size} selected</span>
                  )}
                </div>
                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-2">
                    {bulkDeleteConfirm ? (
                      <>
                        <span className="text-sm text-red-400 mr-2">Delete {selectedIds.size} items?</span>
                        <Button size="sm" onClick={handleBulkDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-bulk-delete-properties">Yes, Delete</Button>
                        <Button size="sm" variant="outline" onClick={() => setBulkDeleteConfirm(false)} className="border-white/20 text-white hover:bg-white/10">Cancel</Button>
                      </>
                    ) : (
                      <Button size="sm" onClick={() => setBulkDeleteConfirm(true)} className="bg-red-600/80 text-white hover:bg-red-600" data-testid="bulk-delete-properties-btn">
                        <Trash2 className="w-4 h-4 mr-1.5" /> Delete Selected ({selectedIds.size})
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((rawProp) => {
              const prop = rawProp;
              const v = adapt(rawProp);
              return (
              <div key={prop.id} className={`glass-card overflow-hidden hover:border-gold/30 transition-colors relative group ${selectedIds.has(prop.id) ? 'border-gold/50 bg-gold/5' : ''}`} data-testid={`property-card-${prop.id}`}>
                {/* Cover image */}
                <div className="relative h-44 bg-gradient-to-br from-white/5 to-white/[0.02]">
                  {v.cover ? (
                    <img src={v.cover} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-text-secondary text-xs">
                      <Building2 className="w-10 h-10 opacity-30" />
                    </div>
                  )}
                  {/* Selection */}
                  <button onClick={() => toggleSelect(prop.id)} className="absolute top-3 right-3 z-10 bg-black/60 backdrop-blur p-1.5 rounded-md" data-testid={`select-property-${prop.id}`}>
                    {selectedIds.has(prop.id) ? <CheckSquare className="w-4 h-4 text-gold" /> : <Square className="w-4 h-4 text-white/70" />}
                  </button>
                  {/* Status pill */}
                  <span className={`absolute top-3 left-3 px-2 py-1 rounded text-[10px] uppercase tracking-[0.15em] font-semibold ${
                    v.status === 'published' || v.status === 'active'
                      ? 'bg-emerald-500/90 text-black'
                      : v.status === 'draft'
                      ? 'bg-amber-500/90 text-black'
                      : 'bg-white/80 text-black'
                  }`}>
                    {v.status}
                  </span>
                  {/* Completion pill (every listing) */}
                  {typeof completion[prop.id]?.total_pct === 'number' && (
                    <span
                      className={`absolute top-10 left-3 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.15em] font-semibold ${
                        completion[prop.id].total_pct >= 100 ? 'bg-gold text-black' : 'bg-black/70 backdrop-blur text-white/90 ring-1 ring-white/15'
                      }`}
                      data-testid={`completion-pill-${prop.id}`}
                    >
                      {completion[prop.id].total_pct}% complete
                    </span>
                  )}
                  {v.gallery > 0 && (
                    <span className="absolute bottom-3 right-3 bg-black/70 backdrop-blur px-2 py-0.5 rounded text-[10px] text-white/80">
                      +{v.gallery} photos
                    </span>
                  )}
                </div>

                <div className="p-5">
                  <h4 className="font-semibold text-base mb-1 line-clamp-1" title={v.title}>{v.title}</h4>
                  {v.shortDesc && (
                    <p className="text-xs text-text-secondary line-clamp-2 mb-3">{v.shortDesc}</p>
                  )}

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-text-secondary">
                      <MapPin className="w-4 h-4 flex-shrink-0" /> <span className="truncate" title={v.location}>{v.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-text-secondary">
                      <Building2 className="w-4 h-4 flex-shrink-0" /> <span>{v.type}</span>
                    </div>

                    {/* Specs row */}
                    {(v.bedrooms || v.bathrooms || v.parking || v.builtUp) && (
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1 pt-2 border-t border-white/5 text-xs">
                        {v.bedrooms != null && <span className="text-white/80"><strong>{v.bedrooms}</strong> Bed</span>}
                        {v.bathrooms != null && <span className="text-white/80"><strong>{v.bathrooms}</strong> Bath</span>}
                        {v.parking != null && <span className="text-white/80"><strong>{v.parking}</strong> Parking</span>}
                        {v.builtUp != null && <span className="text-white/80"><strong>{v.builtUp}</strong> {v.areaUnit}</span>}
                      </div>
                    )}

                    {/* Feature counters */}
                    {(v.amenities || v.smartFeatures || v.views || v.videos) > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-2">
                        {v.amenities > 0 && <span className="px-2 py-0.5 rounded-full bg-white/5 text-[10px] text-white/70">{v.amenities} amenities</span>}
                        {v.smartFeatures > 0 && <span className="px-2 py-0.5 rounded-full bg-white/5 text-[10px] text-white/70">{v.smartFeatures} smart features</span>}
                        {v.views > 0 && <span className="px-2 py-0.5 rounded-full bg-white/5 text-[10px] text-white/70">{v.views} views</span>}
                        {v.videos > 0 && <span className="px-2 py-0.5 rounded-full bg-gold/10 text-[10px] text-gold">{v.videos} videos</span>}
                      </div>
                    )}

                    {v.rera && (
                      <div className="text-[10px] text-text-secondary pt-1">
                        RERA: <span className="text-white/70 font-mono">{v.rera}</span>
                      </div>
                    )}

                    {/* Legacy unit availability */}
                    {v.totalUnits != null && (
                      <div className="pt-3 border-t border-white/10">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-text-secondary text-xs">Available</span>
                          <span className="text-gold font-bold text-sm">{v.availableUnits}/{v.totalUnits}</span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2">
                          <div
                            className="bg-gold rounded-full h-2"
                            style={{ width: `${((v.totalUnits - v.availableUnits) / v.totalUnits) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Price */}
                    <div className="flex justify-between items-baseline pt-3 border-t border-white/10">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-text-secondary">Price</div>
                        <div className="text-gold font-semibold text-base">{v.priceLabel}</div>
                      </div>
                      {v.pricePerSqft && (
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-[0.15em] text-text-secondary">/ Sq.Ft.</div>
                          <div className="text-white/80 text-sm">{formatCurrency(v.pricePerSqft)}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Edit / Delete Actions */}
                  <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                    <Button variant="outline" size="sm" onClick={() => handleEditProperty(prop)} className="flex-1 border-white/20 text-white hover:bg-white/10 hover:text-gold" data-testid={`edit-property-${prop.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                    {deleteConfirm === prop.id ? (
                      <div className="flex gap-1 flex-1">
                        <Button size="sm" onClick={() => handleDeleteProperty(prop.id)} className="flex-1 bg-red-600 text-white hover:bg-red-700" data-testid={`confirm-delete-${prop.id}`}>Yes</Button>
                        <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 border-white/20 text-white hover:bg-white/10">No</Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(prop.id)} className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid={`delete-property-${prop.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
            {properties.length === 0 && (
              <div className="col-span-3 glass-card p-12 text-center">
                <Building2 className="w-16 h-16 text-gold mx-auto mb-4 opacity-50" />
                <h3 className="font-playfair text-2xl font-semibold mb-2">No Projects Yet</h3>
                <p className="text-text-secondary mb-6">Add your first property project to get started</p>
                <Button onClick={() => navigate('/partner/listings/new/property')} className="bg-gold text-black hover:bg-gold-light">
                  <Plus className="w-4 h-4 mr-2" /> Add Property
                </Button>
              </div>
            )}
          </div>
          </div>
        )}

        {/* CRM Tab */}
        {activeTab === 'crm' && (
          <div className="space-y-6">
            <PartnerCRMDashboardV2 />
            <PartnerCRM partnerId={user?.id} />
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="glass-card p-8 text-center">
            <BarChart3 className="w-16 h-16 text-gold mx-auto mb-4 opacity-50" />
            <h3 className="font-playfair text-2xl font-semibold mb-2">Reports Coming Soon</h3>
            <p className="text-text-secondary">Detailed analytics and export functionality will be available soon.</p>
          </div>
        )}
      </div>

      {/* Add Property Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-lenis-prevent>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="glass-card p-8 max-w-lg w-full max-h-[85vh] overflow-y-auto overscroll-contain"
            style={{ scrollbarGutter: 'stable' }}
            data-lenis-prevent
          >
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-obsidian/90 backdrop-blur-sm pb-4 -mt-2 pt-2 z-10">
              <h2 className="font-playfair text-2xl font-semibold">{editingProperty ? 'Edit Property' : 'Add New Property'}</h2>
              <Button variant="ghost" onClick={() => { setShowAddModal(false); setEditingProperty(null); }}><X className="w-5 h-5" /></Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Project Name *</Label>
                <Input value={newProperty.project_name} onChange={(e) => setNewProperty({...newProperty, project_name: e.target.value})} className="bg-white text-black" />
              </div>
              <div>
                <Label>Location *</Label>
                <Input value={newProperty.location} onChange={(e) => setNewProperty({...newProperty, location: e.target.value})} className="bg-white text-black" placeholder="Dubai Marina" />
              </div>
              <div>
                <Label>Property Type</Label>
                <StyledSelect
                  value={newProperty.property_type}
                  onChange={(v) => setNewProperty({...newProperty, property_type: v})}
                  options={[
                    { value: 'apartment', label: 'Apartment' },
                    { value: 'villa', label: 'Villa' },
                    { value: 'townhouse', label: 'Townhouse' },
                    { value: 'penthouse', label: 'Penthouse' },
                    { value: 'commercial', label: 'Commercial' },
                  ]}
                  data-testid="dev-new-property-type-select"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Units</Label>
                  <Input type="number" value={newProperty.total_units} onChange={(e) => setNewProperty({...newProperty, total_units: parseInt(e.target.value)})} className="bg-white text-black" />
                </div>
                <div>
                  <Label>Available Units</Label>
                  <Input type="number" value={newProperty.available_units} onChange={(e) => setNewProperty({...newProperty, available_units: parseInt(e.target.value)})} className="bg-white text-black" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price From (AED)</Label>
                  <Input type="number" value={newProperty.price_from} onChange={(e) => setNewProperty({...newProperty, price_from: parseFloat(e.target.value)})} className="bg-white text-black" />
                </div>
                <div>
                  <Label>Price To (AED)</Label>
                  <Input type="number" value={newProperty.price_to} onChange={(e) => setNewProperty({...newProperty, price_to: parseFloat(e.target.value)})} className="bg-white text-black" />
                </div>
              </div>
              <div>
                <Label>Completion Date</Label>
                <Input type="date" value={newProperty.completion_date} onChange={(e) => setNewProperty({...newProperty, completion_date: e.target.value})} className="bg-white text-black" />
              </div>
              <div>
                <Label>Description</Label>
                <textarea value={newProperty.description} onChange={(e) => setNewProperty({...newProperty, description: e.target.value})} className="w-full bg-white text-black rounded p-3 min-h-[100px]" />
              </div>
              <div className="pt-4 pb-2">
                <Button onClick={handleCreateProperty} disabled={!newProperty.project_name || !newProperty.location} className="w-full bg-gold text-black hover:bg-gold-light">
                  {editingProperty ? 'Update Property' : 'Create Property'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-lenis-prevent>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="glass-card p-8 max-w-3xl w-full max-h-[85vh] overflow-y-auto overscroll-contain"
            data-lenis-prevent
            data-testid="bulk-upload-modal"
          >
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-obsidian/90 backdrop-blur-sm pb-4 -mt-2 pt-2 z-10">
              <h2 className="font-playfair text-2xl font-semibold">Bulk Property Upload</h2>
              <Button variant="ghost" onClick={() => setShowBulkUpload(false)}><X className="w-5 h-5" /></Button>
            </div>
            <BulkUpload onComplete={() => { setShowBulkUpload(false); fetchData(); }} />
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DeveloperDashboard;
